import './style.scss';

import { createContext, useEffect } from 'react';

import InfoCard from '@/components/Shared/InfoCard';
import { SystemNotifications } from '@/components/Shared/entities';
import { AllurLoader, CustomTab } from '@/components/Shared/ui';
import { LEASING_STATUSES } from '@/constants';
import QuestionIcon from '@/media/icons/question-circle-outlined.svg?react';
import UserIcon from '@/media/icons/user.svg?react';
import { formatPhoneNumber, formatPrice } from '@/services/formatter';
import { getUserFullName } from '@/services/helpers/strings';
import { useAppDispatch, useAppSelector } from '@/store';
import { setHeaderConfig } from '@/store/app.slice';

import useLeasingRequestId from '../hooks/useLeasingRequestId';
import useFetchOrderData from './hooks/useFetchOrderData';
import { resetLlpLeasingOrder, setActiveTabIndex } from './store';
import { LeasingCardMobile } from './ui';
import { CreateTab, OffersTab } from './widgets';

export const CreateTabContext = createContext({ isFieldsFrozen: false });

const checkedTabs: Record<string, number[]> = {
  leasing_draft: [],
  financed: [0, 1]
};
const TABS: { title: string; key: string }[] = [
  {
    key: 'order',
    title: 'Заявка'
  },
  {
    key: 'offer',
    title: 'Предложения'
  }
];

const LlpOrder = () => {
  const dispatch = useAppDispatch();

  const { requestId, isCreateMode } = useLeasingRequestId();

  const { request, refreshToken, activeTabIndex } = useAppSelector(
    (state) => state.llpLeasingOrder
  );

  const { fetchOrderData, isLoading } = useFetchOrderData();

  const isFieldsFrozen: boolean =
    !!request && request.status.code !== 'leasing_draft';

  const handleStepCurrentChange = (targetTabIndex: number) => {
    if (
      isCreateMode ||
      !request ||
      request.status.code === 'leasing_draft' ||
      targetTabIndex === -1
    )
      return;

    dispatch(setActiveTabIndex(targetTabIndex));
  };

  useEffect(() => {
    dispatch(
      setHeaderConfig({
        title: 'Заявка (ТОО)',
        helmetTitle: `Лизинг - Заявка (ТОО) ${requestId}`,
        hasBackArrow: true
      })
    );

    return () => {
      dispatch(setHeaderConfig({}));
    };
  }, [requestId]);

  useEffect(() => {
    if (isCreateMode) return;
    fetchOrderData();
  }, [refreshToken, isCreateMode]);

  useEffect(() => {
    return () => {
      dispatch(resetLlpLeasingOrder());
    };
  }, []);

  if (isLoading) return <AllurLoader fullScreen />;

  const statusColors =
    LEASING_STATUSES[request?.status.code as keyof typeof LEASING_STATUSES];

  return (
    <div className="create-leasing">
      <div className="create-leasing__tab-container">
        <CustomTab
          activeTab={TABS[activeTabIndex].key}
          setActiveTab={(targetTabKey) =>
            handleStepCurrentChange(
              TABS.findIndex((item) => item.key === targetTabKey)
            )
          }
          tabs={TABS}
          alwaysChecked={checkedTabs[request?.status.code || ''] ?? [0]}
          finishedText={
            request?.status.code === 'financed' ? 'Профинансирован' : undefined
          }
        />
      </div>

      <SystemNotifications marginFrom="top" />

      {!!request && (
        <div className="create-leasing__header">
          <InfoCard
            title={request.companyName}
            subtitle={`ИИН/БИН: ${request.iin}`}
          />
          <InfoCard
            title={'Сумма лизинга'}
            subtitle={formatPrice(request.totalAmount || 0)}
          />
          <InfoCard
            title={getUserFullName(request.client) || request.clientFullName}
            subtitle={formatPhoneNumber(request.contactPhoneNumber)}
            icon={<UserIcon />}
          />
          <InfoCard
            subtitle={request.status.name}
            title={`ID #${request.code || 0}`}
            circleColor={statusColors.bgColor}
            icon={<QuestionIcon />}
          />
        </div>
      )}

      {!!request && (
        <div className="create-leasing__header-mobile">
          <LeasingCardMobile
            title={request.companyName}
            subtitle={`ИИН/БИН: ${request.iin}`}
            statusName={request.status.name}
            statusCode={request.status.code}
          />
        </div>
      )}

      <div className="create-leasing__content">
        {activeTabIndex === 0 && (
          <CreateTabContext.Provider value={{ isFieldsFrozen }}>
            <CreateTab />
          </CreateTabContext.Provider>
        )}
        {activeTabIndex === 1 && <OffersTab />}
      </div>
    </div>
  );
};

export default LlpOrder;
